import {
  isSentinel,
  sentinelLabel,
  formatCurrency,
  formatRateGuarantee,
  enumLabel,
  INCLUSION_LABELS,
  ANNUAL_OPEN_ENROLLMENT_LABELS,
  formatPreExistingConditionMonthsDetail,
} from "./shared";

export type HospitalFieldFormatter = (row: Record<string, unknown>) => string;

const dollarAmountTyped =
  (typeKey: string, amountKey: string): HospitalFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);
    if (type === "dollarAmount") return formatCurrency(row[amountKey]) || "";
    return "";
  };

/** Extended (non-v5) fields — free text, passed through as-is. */
const freeText =
  (fieldKey: string): HospitalFieldFormatter =>
  (row) => {
    const value = row[fieldKey];
    return typeof value === "string" ? value : "";
  };

/**
 * Pregnancy is a dropdown now (CD-2076/CD-2542), but it started as free text
 * and existing plans still carry whatever an analyst typed. Label the four
 * known dropdown values; fall back to the raw stored value — display-as-is,
 * per the spreadsheet — for anything else, so legacy data still reaches the
 * sheet instead of a plain `enumLabel` blanking (and dropping) the row.
 */
const formatPregnancy: HospitalFieldFormatter = (row) => {
  const value = row["pregnancy"];
  if (typeof value !== "string" || value === "") return "";
  if (isSentinel(value)) return sentinelLabel(value);
  return INCLUSION_LABELS[value] ?? value;
};

/** Pre-existing condition — slash-joined month string or sentinel label. */
const formatPreExistingCondition: HospitalFieldFormatter = (row) => {
  const type = row["preExistingConditionType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  if (type === "waived") return "Waived";
  if (type === "none") return "None";
  if (type !== "months") return "";
  const detail = row["preExistingConditionMonthsDetail"];
  return typeof detail === "string" && detail
    ? formatPreExistingConditionMonthsDetail(detail)
    : "";
};

export const hospitalFormatters: Record<string, HospitalFieldFormatter> = {
  hospitalAdmission: dollarAmountTyped(
    "hospitalAdmissionType",
    "hospitalAdmissionAmount",
  ),
  icuAdmission: dollarAmountTyped("icuAdmissionType", "icuAdmissionAmount"),
  hospitalConfinement: dollarAmountTyped(
    "hospitalConfinementType",
    "hospitalConfinementAmount",
  ),
  icuConfinement: dollarAmountTyped(
    "icuConfinementType",
    "icuConfinementAmount",
  ),
  wellness: dollarAmountTyped("wellnessType", "wellnessAmount"),
  preExistingCondition: formatPreExistingCondition,
  portability: enumLabel("portability", INCLUSION_LABELS),
  annualOpenEnrollment: enumLabel(
    "annualOpenEnrollment",
    ANNUAL_OPEN_ENROLLMENT_LABELS,
  ),
  rateGuarantee: formatRateGuarantee,
  // Extended (non-v5) fields. The value is already display-ready, but the MP
  // export drops any row whose formatter returns undefined (`fieldValues` in
  // marketing-presentation-proposal.ts), so an entry is what puts these rows
  // on the sheet at all.
  eligibility: freeText("eligibility"),
  // 24-hour Coverage and Newborn/Nursery Care are dropdowns now, not free
  // text — their stored value is an enum key that needs the same label as
  // the grid. Pregnancy is too (see formatPregnancy above), but unlike
  // these two it has pre-dropdown free-text data still on real plans, so
  // it needs the raw-value fallback instead of a plain enumLabel.
  twentyFourHourCoverage: enumLabel("twentyFourHourCoverage", INCLUSION_LABELS),
  pregnancy: formatPregnancy,
  newbornNurseryCare: enumLabel("newbornNurseryCare", INCLUSION_LABELS),
  eligibilityHours: freeText("eligibilityHours"),
};

export const formatHospitalField = (
  fieldId: string,
  row: Record<string, unknown>,
): string | undefined => {
  const formatter = hospitalFormatters[fieldId];
  return formatter ? formatter(row) : undefined;
};
